import { api } from "@/shared/api";
import type { AdvisorTurnData, AiEnvelope } from "../model/chatTypes";
import type { UploadRef } from "../model/advisorUploads";
import type { SessionState } from "../model/contextState";

// backend ApiResponse 已被 @/shared/api 解一层(返 json.data = aiMap{success,data});此处再解一层拿 AdvisorTurnData。
async function unwrap(p: Promise<AiEnvelope<AdvisorTurnData>>): Promise<AdvisorTurnData> {
  return (await p).data;
}

/**
 * PC 军师对话 thin api。复用 P4 转发器 `AdvisorController` → ai `/turn`、`/turn/resume`、`/session/open`。
 * **不** import advisor-h5(Option B 守「不动 advisor-h5」红线);**不** import `@/features/*`。
 */
export const advisorChatApi = {
  // uploadedImages=本条消息附的上传图(走 ai 既有契约,不新造字段;仅在有图时进 body,
  // 保持无附图时请求体逐字不变 → 契约向后兼容)。
  chat: (sessionId: string, message: string, uploadedImages?: UploadRef[] | null) =>
    unwrap(
      api.post<AiEnvelope<AdvisorTurnData>>("/advisor/chat", {
        sessionId,
        message,
        ...(uploadedImages && uploadedImages.length ? { uploadedImages } : {}),
      })
    ),
  // resume 要**重发**上传图集:ai 侧 ctx 每次重建,不重发则确认门后那张图就找不到了。
  resume: (
    sessionId: string,
    confirmed: boolean,
    pendingId: string,
    uploadedImages?: UploadRef[] | null
  ) =>
    unwrap(
      api.post<AiEnvelope<AdvisorTurnData>>("/advisor/turn/resume", {
        sessionId,
        confirmed,
        pendingId,
        ...(uploadedImages && uploadedImages.length ? { uploadedImages } : {}),
      })
    ),
  // C2:确认门风格条点卡 → 确定性 patch 本 pending 的 style 后重报价(不经 LLM turn),返回刷新后的确认门。
  pickStyle: (sessionId: string, styleKey: string, pendingId: string) =>
    unwrap(
      api.post<AiEnvelope<AdvisorTurnData>>("/advisor/turn/pick-style", {
        sessionId,
        styleKey,
        pendingId,
      })
    ),
  /**
   * 会话上下文状态探针(只读:不建 session、不烧 LLM)。
   * ★**只有一层信封** —— api 侧代理口已经把 ai 那层解掉了(与 /advisor/briefing 同写法),
   * `httpClient` 解掉 `json.data` 之后**直接就是 DTO**。实测原样响应:
   *   {"success":true,"data":{"sessionId":…,"contextState":"restored",…},"message":"OK"}
   * 按 chat/resume 那样再解一层会拿到 undefined **而且不报错** —— 判据会永远走
   * 「认不出」分支,界面天天弹"没接回来"。同仓两种约定,逐口确认,不许类推。
   */
  sessionState: (sessionId: string) =>
    api.get<SessionState>(`/advisor/session/state?sessionId=${encodeURIComponent(sessionId)}`),
  /**
   * 从历史点开继续。**POST**:它会**写**会话状态(把当前话题切过去 + 回灌上下文)。
   * `topicId` 可选:不带 = 续当前那条;带 = 切到那条。
   * 回包与 `/advisor/session/state` **完全同一个 shape、同样一层信封**,解析共用。
   * ★不要拿 HTTP 200 当"切成功了" —— 判据是 `topicId === requestedTopicId`(见 contextState)。
   */
  continueTopic: (sessionId: string, topicId?: string | null) =>
    api.post<SessionState>(
      `/advisor/topic/continue?sessionId=${encodeURIComponent(sessionId)}` +
        (topicId ? `&topicId=${encodeURIComponent(topicId)}` : ""),
      {}
    ),
  sessionOpen: (sessionId: string) =>
    unwrap(
      api.post<AiEnvelope<AdvisorTurnData>>("/advisor/session/open", { sessionId, message: "" })
    ),
  // 开新话题:清 ai 工作上下文(sessionId 后端从身份重建,前端无需传)。
  newTopic: () =>
    api.post<AiEnvelope<{ ok: boolean }>>("/advisor/session/new-topic", {}).then((r) => r.data),
};
